/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { uuid } from '@src/app/model/abstract-object';
import { Team } from '@src/app/model/team';
import { User } from '@src/app/model/user';
import { Role } from '@src/app/model/role';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { AvatarComponent } from '@src/app/modules/user/avatar/avatar.component';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { MyInjector } from '@src/app/app.module';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '@src/app/shared/tenant.service';
import { MessageService } from '@src/app/shared/message.service';
import { UserProfileNavRoute } from '@src/app/routes/app-router';
import { buildDate, buildVersion } from '@src/environments/version';
@Component({
    selector: 'app-user-nav-button-component',
    templateUrl: './user-nav-button-component.component.html',
    styleUrls: ['./user-nav-button-component.component.scss'],
    imports: [AvatarComponent, MatMenuModule, MatButtonModule, MatIconModule, MatDividerModule, RouterLink]
})
export class UserNavButtonComponentComponent {
    currentUserSvc = inject(CurrentUserService);
    private tenantSvc = inject(TenantService);
    private omcSettingsSvc = inject(OmcSettingsService);


    currentUser: User;
    currentTeam: uuid;
    currentTeamName: string;
    showOmcDashboard = 'no';

    buildDate = buildDate;
    buildVersion = buildVersion;    
    /**
     * @deprecated Teams array replaced by active positions
     */
    teams: Team[];
    activePositions: PersonUnitRole[];

    profilePath = '/' + UserProfileNavRoute.openRoute.path;

    currentPath = '';
    currentHost = window.location.hostname;

    constructor() {
        const currentUserSvc = this.currentUserSvc;
        const router = inject(Router);

        router.events.pipe(takeUntilDestroyed()).subscribe(o => {
            if (o instanceof NavigationEnd) {
                this.currentPath = o.urlAfterRedirects;
            }
        })
        this.currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe(currentUser => {
            this.currentUser = currentUser;
            if (currentUser) {
                this.activePositions = currentUserSvc.getActivePositions();
                this.currentTeam = currentUser.current_team_id;
                this.currentTeamName = this.currentUser?.currentTeam?.name;
                this.loadOmcDashboardSetting();
            }
        });
    }

    /**
     * Directors can be granted dashboard access via the OMC's showOmcDashboard setting.
     * Agents always have access, so we only need the setting for a director in an OMC.
     */
    private loadOmcDashboardSetting() {
        if (this.currentUser?.inAgency || this.currentUser?.fromAgentTeamId) {
            return;
        }
        if (this.currentUser?.currentRole?.globalRoleId !== Role.ROLE.DIRECTOR.id) {
            return;
        }
        this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null).subscribe(
            settings => this.showOmcDashboard = settings?.showOmcDashboard
        );
    }

    /** Whether the dashboard menu links should be shown for the current user. */
    showDashboard(): boolean {
        if (this.currentUser?.inAgency || this.currentUser?.fromAgentTeamId) {
            return true;
        }
        if (this.currentUser?.currentRole?.globalRoleId === Role.ROLE.DIRECTOR.id) {
            if (this.showOmcDashboard === 'directors') {
                return true;
            }
        }
        return false;
    }

    onDashboard() {
        if (this.currentPath === this.dashboardPath()) {
            return true;
        }
        return false;
    }

    dashboardPath() : string{
        return `/${this.tenantSvc.getCurrentTenantId()}/home`;
    }
    getIssueUrl() {
        if (this.currentUser) {
            return `https://docs.google.com/forms/d/e/1FAIpQLSdS4q0H5UXhbQ1TKx6IMRVu-HfZRbjaq0bSDPnxDZmbeYyhyQ/viewform?usp=pp_url&entry.954273259=${this.currentUser.name}&entry.1107363702=${this.currentPath}&entry.233045424=${this.currentTeamName}&entry.2033988018=${this.currentHost}`
        } else {
            return '';
        }

    }
    getIssueLogUrl() {
        if (this.currentUser) {
            return `https://docs.google.com/spreadsheets/d/1WtoJ6IgeTP9xmKsBXJC0XkjEta7wLyM-TmgQmQMDrNw/edit#gid=198211854`
        } else {
            return '';
        }
    }

    impersonate() {
        const cds = MyInjector.instance.get(ConfirmDialogService);
        const msgSvc = MyInjector.instance.get(MessageService);
        const http = MyInjector.instance.get(HttpClient);
        if (this.currentUserSvc.hasUber()) {
            const cd = {
                title: 'Impersonate Someone',
                msg: 'Who would you like to impersonate?',
                needsReason: true,
                options: [{name: 'Yes Please', needsReason: true, action: (who) => {
                    http.post(TenantService.bareAjaxPath + 'uber/imperson8', {who}).subscribe( o => {
                        console.warn("Result was ", o);
                        msgSvc.show('Away you go as ' + who + " you will need to refresh browser");
                    }, f => msgSvc.showError(f, "impersonating"));
                }}]
            }
            cds.openChoice(cd, true);
        }
    }

    logoutCurrentUser() {
        this.currentUserSvc.logoutCurrentUser();
    }

    selectCurrentTeam(pur: PersonUnitRole) {
        this.currentUserSvc.switchTeamRole(pur);
    }

}
